import * as Popover from "@radix-ui/react-popover";
import { Check, ChevronDown, Maximize2, Paperclip } from "lucide-react";
import { CSSProperties, useMemo, useState } from "react";
import { flushSync } from "react-dom";
import { Link, useNavigate } from "react-router";

import { StatusPill, type StatusPillTone } from "nazare-ui/components";
import { cn } from "nazare-ui/utils";

import { ScrollShadow, TABLE_HEADER_HEIGHT } from "../../../scroll-shadow";
import { SkeletonRow } from "../../../skeleton-row";
import { useScrollEdges } from "../../../use-scroll-edges";
import { useSimulatedLoad } from "../../../use-simulated-load";
import styles from "./runs-table.module.css";

type RunStatus = "queued" | "running" | "completed";

type WorkflowRun = {
  /** `wrun_<ULID>` — same id shape as the reference dashboard. */
  id: string;
  status: RunStatus;
  workflowName: string;
  /** Muted secondary line under the workflow name — the mailbox, company,
   * channel, or "system" that the run relates to/was triggered by. */
  secondary: string;
  /** Hardcoded display string, not a `Date` — Workflow scripts forbid
   * non-deterministic calls like `new Date()`/`Date.now()`. */
  createdAt: string;
  artefactCount: number;
  /** `null` renders as "—" (queued/still-running runs have no duration yet). */
  duration: string | null;
};

const STATUS_LABEL: Record<RunStatus, string> = {
  queued: "Queued",
  running: "Running",
  completed: "Completed",
};

const STATUS_TONE: Record<RunStatus, StatusPillTone> = {
  queued: "neutral",
  running: "info",
  completed: "success",
};

/** Static mock runs, newest first — there is no live workflow-orchestration
 * backend in `platform` yet. Shaped after the real `/admin/workflows` runs
 * table (workflow names, ULID-style run ids, realistic recent timestamps). */
const RUNS: WorkflowRun[] = [
  {
    id: "wrun_01KX2N8H4VQTG6R3MC7ZP9F2AD",
    status: "running",
    workflowName: "ingestGmailInbox",
    secondary: "dealflow@phoenixcourt.vc",
    createdAt: "07/07/2026, 22:32:32",
    artefactCount: 0,
    duration: null,
  },
  {
    id: "wrun_01KX2MZ3D8JSY5T1QN6HRB4E7K",
    status: "completed",
    workflowName: "identifyCompanyUpdate",
    secondary: "component-labs",
    createdAt: "07/07/2026, 22:18:04",
    artefactCount: 3,
    duration: "2m 41s",
  },
  {
    id: "wrun_01KX2MYQ7F2VD9G4TS8HNC3B6R",
    status: "queued",
    workflowName: "sendNotification",
    secondary: "partners@phoenixcourt.vc",
    createdAt: "07/07/2026, 22:15:50",
    artefactCount: 0,
    duration: null,
  },
  {
    id: "wrun_01KX2MXG5T3NQ7H2VD8FRC4S9M",
    status: "completed",
    workflowName: "syncCalendarEvents",
    secondary: "thomas@phoenixcourt.vc",
    createdAt: "07/07/2026, 21:58:12",
    artefactCount: 1,
    duration: "18s",
  },
  {
    id: "wrun_01KX2MW94RJ6T3N8HQVD2FC7SB",
    status: "completed",
    workflowName: "refreshPortfolioMetrics",
    secondary: "system",
    createdAt: "07/07/2026, 21:40:37",
    artefactCount: 5,
    duration: "4m 06s",
  },
  {
    id: "wrun_01KX2MV27HQD9F4TN6R3VC8S5J",
    status: "running",
    workflowName: "enrichCompanyProfile",
    secondary: "nebula-robotics",
    createdAt: "07/07/2026, 21:36:02",
    artefactCount: 0,
    duration: null,
  },
  {
    id: "wrun_01KX2MTS6NF3Q8H2VD7RC4T9GB",
    status: "completed",
    workflowName: "scoreDealSentiment",
    secondary: "orbital-health",
    createdAt: "07/07/2026, 21:12:45",
    artefactCount: 2,
    duration: "56s",
  },
  {
    id: "wrun_01KX2MSK4VQ7H3N9TD2FRC6S8M",
    status: "completed",
    workflowName: "ingestGmailInbox",
    secondary: "dealflow@phoenixcourt.vc",
    createdAt: "07/07/2026, 20:47:19",
    artefactCount: 4,
    duration: "3m 22s",
  },
  {
    id: "wrun_01KX2MRB3TN8Q6H4VD9FRC2S7J",
    status: "queued",
    workflowName: "notifyPartnerSlack",
    secondary: "#deal-flow",
    createdAt: "07/07/2026, 20:44:58",
    artefactCount: 0,
    duration: null,
  },
  {
    id: "wrun_01KWZK92H7TQ3N6F4VD8RC5S2M",
    status: "completed",
    workflowName: "generateWeeklyDigest",
    secondary: "system",
    createdAt: "07/07/2026, 08:05:11",
    artefactCount: 6,
    duration: "5m 51s",
  },
  {
    id: "wrun_01KWYKA11QB4V3JRFCSP643N8B",
    status: "completed",
    workflowName: "archiveStaleThreads",
    secondary: "system",
    createdAt: "06/07/2026, 23:59:47",
    artefactCount: 12,
    duration: "1m 03s",
  },
  {
    id: "wrun_01KWY7F2S9NQ4T6H3VDRC8B5MJ",
    status: "completed",
    workflowName: "backupKnowledgeBase",
    secondary: "system",
    createdAt: "06/07/2026, 19:30:02",
    artefactCount: 1,
    duration: "9m 14s",
  },
  {
    id: "wrun_01KWY6D8R4TN3Q7H9VFC2S6BMJ",
    status: "completed",
    workflowName: "reindexSearchCorpus",
    secondary: "system",
    createdAt: "06/07/2026, 14:02:39",
    artefactCount: 0,
    duration: "42s",
  },
  {
    id: "wrun_01KWY5C3Q7NT8F4H2VDRC9S6BJ",
    status: "completed",
    workflowName: "pruneExpiredArtefacts",
    secondary: "system",
    createdAt: "05/07/2026, 07:10:22",
    artefactCount: 8,
    duration: "1m 27s",
  },
];

/** `queued`/`running`/`completed` (this file's `RunStatus`) plus `failed` —
 * offered as a filter option even though no mock row currently uses it,
 * matching the reference dashboard's status filter (queued/running/
 * completed/failed). Kept separate from `RunStatus`/`STATUS_LABEL` so the
 * pill-rendering types stay exactly as they were. */
type StatusFilterValue = RunStatus | "failed";

const STATUS_FILTER_LABEL: Record<StatusFilterValue, string> = {
  queued: "Queued",
  running: "Running",
  completed: "Completed",
  failed: "Failed",
};

const STATUS_FILTER_VALUES: StatusFilterValue[] = [
  "queued",
  "running",
  "completed",
  "failed",
];

/** Distinct workflow names present in `RUNS`, in order of first appearance —
 * the "All workflows" dropdown's option list. */
const WORKFLOW_FILTER_VALUES: string[] = Array.from(
  new Set(RUNS.map((run) => run.workflowName)),
);

type FilterOption<T extends string> = {
  /** `null` = the reset ("All …") option. */
  value: T | null;
  label: string;
};

type FilterDropdownProps<T extends string> = {
  options: FilterOption<T>[];
  value: T | null;
  onChange: (value: T | null) => void;
  "aria-label": string;
};

/**
 * Small filter dropdown for the Runs table's header bar — mirrors the
 * Popover.Root/Trigger/Portal/Content structure (and .content/.list/.option/
 * .optionActive/.checkIcon styling convention) used by the nav rail's
 * `<ThemeToggle>`/`<NotificationBell>` popovers and the full-page
 * `/workflows/runs` route's own identical dropdown (`pages/runs.tsx`).
 * Generic over the filter's value type so the same markup drives both the
 * workflow and status dropdowns below. Picking an option both updates the
 * filter and closes the popover.
 */
function FilterDropdown<T extends string>({
  options,
  value,
  onChange,
  "aria-label": ariaLabel,
}: FilterDropdownProps<T>) {
  const [open, setOpen] = useState(false);
  const activeLabel =
    options.find((option) => option.value === value)?.label ??
    options[0]?.label ??
    "";

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button type="button" className={styles.filterButton} aria-label={ariaLabel}>
          {activeLabel}
          <ChevronDown className={styles.filterIcon} aria-hidden="true" />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          side="bottom"
          align="start"
          sideOffset={6}
          className={styles.dropdownContent}
        >
          <ul className={styles.dropdownList} role="listbox">
            {options.map((option) => {
              const isActive = option.value === value;
              return (
                <li key={option.value ?? "__all__"}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={isActive}
                    className={cn(
                      styles.dropdownOption,
                      isActive && styles.dropdownOptionActive,
                    )}
                    onClick={() => {
                      onChange(option.value);
                      setOpen(false);
                    }}
                  >
                    <span className={styles.dropdownOptionLabel}>
                      {option.label}
                    </span>
                    {isActive && (
                      <Check className={styles.checkIcon} aria-hidden="true" />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

const WORKFLOW_FILTER_OPTIONS: FilterOption<string>[] = [
  { value: null, label: "All workflows" },
  ...WORKFLOW_FILTER_VALUES.map((workflowName) => ({
    value: workflowName,
    label: workflowName,
  })),
];

const STATUS_FILTER_OPTIONS: FilterOption<StatusFilterValue>[] = [
  { value: null, label: "All statuses" },
  ...STATUS_FILTER_VALUES.map((status) => ({
    value: status,
    label: STATUS_FILTER_LABEL[status],
  })),
];

/**
 * Bottom "Runs" table from the reference `/admin/workflows` dashboard: a
 * full-width card with a filter-bar header and a table of individual
 * workflow runs (status, workflow, run id, created time, artefact count,
 * duration). The "All workflows"/"All statuses" buttons are real dropdown
 * menus that filter the visible rows client-side (AND-combined), and each
 * row is a real, whole-row link to its run's detail page.
 */
export function WorkflowRunsTable() {
  const navigate = useNavigate();
  const isLoading = useSimulatedLoad();
  const { ref: tableWrapRef, atTop, atBottom } = useScrollEdges<HTMLDivElement>();
  const [workflowFilter, setWorkflowFilter] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilterValue | null>(
    null,
  );

  const visibleRuns = useMemo(
    () =>
      RUNS.filter((run) => {
        if (workflowFilter && run.workflowName !== workflowFilter) return false;
        if (statusFilter && run.status !== statusFilter) return false;
        return true;
      }),
    [workflowFilter, statusFilter],
  );

  const goToFullPage = () => {
    if (!document.startViewTransition) {
      navigate("/workflows/runs");
      return;
    }
    document.startViewTransition(() => flushSync(() => navigate("/workflows/runs")));
  };

  return (
    <section
      className={styles.card}
      style={{ viewTransitionName: "wf-card-runs" } as unknown as CSSProperties}
    >
      <header className={styles.header}>
        <h2 className={styles.title}>Runs</h2>
        <div className={styles.filters}>
          <FilterDropdown
            options={WORKFLOW_FILTER_OPTIONS}
            value={workflowFilter}
            onChange={setWorkflowFilter}
            aria-label="Filter by workflow"
          />
          <FilterDropdown
            options={STATUS_FILTER_OPTIONS}
            value={statusFilter}
            onChange={setStatusFilter}
            aria-label="Filter by status"
          />
          <button
            type="button"
            className={styles.maximiseButton}
            onClick={goToFullPage}
            aria-label="Expand Runs"
            title="Expand Runs"
          >
            <Maximize2 className={styles.maximiseIcon} aria-hidden="true" />
          </button>
        </div>
      </header>

      <div className={styles.scrollShadowWrapper}>
      <div ref={tableWrapRef} className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.th} scope="col">
                Status
              </th>
              <th className={styles.th} scope="col">
                Workflow
              </th>
              <th className={styles.th} scope="col">
                Run ID
              </th>
              <th className={styles.th} scope="col">
                Created
              </th>
              <th className={styles.th} scope="col">
                Artefacts
              </th>
              <th className={`${styles.th} ${styles.thRight}`} scope="col">
                Duration
              </th>
            </tr>
          </thead>
          <tbody>
            {isLoading
              ? Array.from({ length: 6 }).map((_, index) => (
                  <SkeletonRow key={`skeleton-${index}`} columns={6} />
                ))
              : visibleRuns.map((run) => (
                  <tr key={run.id} className={styles.row}>
                    <td className={styles.td}>
                      {/* Stretched-link pattern: a `<tr>` can't be wrapped in an
                         `<a>` (invalid HTML), so this empty, visually-hidden
                         `<Link>` is absolutely positioned to cover the whole row
                         (`.rowLink` = `position: absolute; inset: 0`, `.row` =
                         `position: relative`) — a real `<a href>` for the whole
                         row, supporting cmd/ctrl/middle-click like any other
                         link in this app, not just an onClick handler. */}
                      <Link
                        to={`/workflows/runs/${run.id}`}
                        className={styles.rowLink}
                        aria-label={`View run ${run.id}`}
                      />
                      <StatusPill tone={STATUS_TONE[run.status]}>
                        {STATUS_LABEL[run.status]}
                      </StatusPill>
                    </td>
                    <td className={styles.td}>
                      <div className={styles.workflowName}>{run.workflowName}</div>
                      <div className={styles.workflowSecondary}>{run.secondary}</div>
                    </td>
                    <td className={`${styles.td} ${styles.runId}`}>{run.id}</td>
                    <td className={`${styles.td} ${styles.created}`}>
                      {run.createdAt}
                    </td>
                    <td className={styles.td}>
                      <span className={styles.artefacts}>
                        <Paperclip className={styles.artefactsIcon} aria-hidden="true" />
                        {run.artefactCount}
                      </span>
                    </td>
                    <td
                      className={`${styles.td} ${styles.duration} ${
                        run.duration ? "" : styles.durationEmpty
                      }`}
                    >
                      {run.duration ?? "—"}
                    </td>
                  </tr>
                ))}
          </tbody>
        </table>
      </div>
      <ScrollShadow atTop={atTop} atBottom={atBottom} topOffset={TABLE_HEADER_HEIGHT} />
      </div>
    </section>
  );
}
